import { Button, Field, Input, Stack, Switch } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { useFormik } from "formik";
import { useStores } from "../../../../shared/stores/RootStore";
import { toast } from "../../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../../shared/views/CustomDialog";

const i18prefix = "map:dashboard.saveModal";

interface Props {
  onClose: () => void;
}

const CreateOrUpdateDashboardModalBase: React.FC<Props> = ({ onClose }) => {
  const { dashboardStore, projectStore } = useStores();

  const selectedProject = projectStore.getSelected()!;
  const selectedDashboard = dashboardStore.selectedDashboard;

  const formik = useFormik({
    initialValues: {
      name: selectedDashboard?.name ?? "",
      overwrite: !!selectedDashboard,
    },

    onSubmit: async (values) => {
      const newItems = dashboardStore.dashboardItems;
      const promise = values.overwrite
        ? dashboardStore.updateDashboard(selectedDashboard!.id, {
            name: values.name,
            items: newItems,
          })
        : dashboardStore.createDashboard({
            projectId: selectedProject.id,
            name: values.name,
            items: newItems,
          });

      promise
        .then(() => {
          toast("success", t(`${i18prefix}.toast.success`));
        })
        .catch(() => {
          toast("error", t(`${i18prefix}.toast.error`));
        })
        .finally(() => onClose());
    },
  });

  const { name, overwrite } = formik.values;
  const nameIsValid = overwrite || name !== (selectedDashboard?.name ?? "");

  return (
    <CustomDialog
      onClose={onClose}
      header={t(`${i18prefix}.header`)}
      footer={
        <Button
          disabled={!nameIsValid}
          flexShrink={0}
          colorPalette="primary"
          type="submit"
          form="createOrUpdateDashboardScenario"
        >
          {selectedDashboard && overwrite
            ? t(`${i18prefix}.submit.update`)
            : t(`${i18prefix}.submit.create`)}
        </Button>
      }
    >
      <form id="createOrUpdateDashboardScenario" onSubmit={formik.handleSubmit}>
        <Stack gap={4}>
          <Field.Root required invalid={!nameIsValid}>
            <Field.Label>{t(`${i18prefix}.name`)}</Field.Label>
            <Input
              name="name"
              onChange={formik.handleChange}
              value={name}
              autoFocus
            />
            <Field.ErrorText>
              {t(`${i18prefix}.error.duplicatedName`)}
            </Field.ErrorText>
          </Field.Root>

          {!!selectedDashboard && (
            <Switch.Root
              colorPalette={"primary"}
              name="overwrite"
              checked={overwrite}
              onChange={formik.handleChange}
            >
              <Switch.HiddenInput />
              <Switch.Control />
              <Switch.Label>
                {t(`${i18prefix}.overwrite`, {
                  name: selectedDashboard.name,
                })}
              </Switch.Label>
            </Switch.Root>
          )}
        </Stack>
      </form>
    </CustomDialog>
  );
};

export const CreateOrUpdateDashboardModal = observer(
  CreateOrUpdateDashboardModalBase
);
